"use client";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { useRouter } from "next/navigation";

import { SearchContextType, SearchResult } from "@/types/helpDesk";

import { httpRequest } from "@/lib/http";

import { useAuth } from "@/context/util/auth";
import { useUi } from "@/context/util/ui";

const SearchContext = createContext<SearchContextType>(null!);

export function SearchProvider({ children }: { children: ReactNode }) {
  const {
    needsBackgroundColorUpdate,
    setBackgroundColor,
    setLoading,
    setSnackbar,
  } = useUi();
  const { authorization, getAccessToken } = useAuth();
  const router = useRouter();

  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  useEffect(() => {
    const newBackgroundColor = "primary.main";
    if (needsBackgroundColorUpdate(newBackgroundColor))
      setBackgroundColor(newBackgroundColor);

    if (!authorization) setLoading(true);
  }, [
    authorization,
    needsBackgroundColorUpdate,
    setBackgroundColor,
    setLoading,
  ]);

  useEffect(() => {
    if (!authorization) return;
    if (authorization.hasAuth === false) {
      router.push("/help-desk/unauthorized");
    } else setLoading(false);
  }, [authorization, router, setLoading]);

  async function getUserSearchResults(search: string) {
    try {
      const token = await getAccessToken();

      if (!token) throw new Error("No access token available");

      const url = `/account-utilities/api/help-desk/search?q=${search}`;

      const headers = { Authorization: `Bearer ${token}` };
      const method = "GET";

      const requestInit: RequestInit = { headers, method };

      const searchResultsResponse = (
        await httpRequest<SearchResult[]>(url, requestInit)
      ).data;

      if (!searchResultsResponse) throw new Error("Empty http response");

      const searchResults = searchResultsResponse.map((sr) => {
        const tooltip =
          sr.userIndicator === "EM"
            ? "Employee"
            : sr.userIndicator === "ST"
            ? "Student"
            : sr.userIndicator === "AF" || sr.userIndicator === "DA"
            ? "Affiliate"
            : "Other";

        return { ...sr, tooltip };
      });

      setSearchResults(searchResults);

      const message = `${searchResults.length} users found matching search ${search}`;
      setSnackbar((prev) => ({
        ...prev,
        message,
        open: true,
        severity: "success",
      }));
    } catch (error) {
      setSearchResults([]);

      const message =
        "Failed to get user search results. Please try again later.";
      console.error(message, error);

      setSnackbar((prev) => ({
        ...prev,
        message,
        open: true,
        severity: "error",
      }));
    }
  }

  function handleRowClick(
    row: SearchResult,
    event: React.MouseEvent<HTMLTableRowElement>
  ) {
    if (
      (authorization?.studentOnly && row.userIndicator !== "ST") ||
      (authorization?.k12Only && row.userIndicator !== "AF")
    ) {
      return;
    }

    const username = row.username;

    setLoading(true);

    router.push(`/help-desk/user?username=${username}`);
  }

  return (
    <SearchContext.Provider
      value={{
        getUserSearchResults,
        handleRowClick,
        searchResults,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch must be used within a SearchContext");
  return ctx;
}
